import React from 'react'

import {
  Box,
  Display,
  FlexDirection,
  JustifyContent,
  ResponsiveValue,
} from '@gousto-internal/citrus-react'
import type { RecipeGridContentBlock, StandardRecipeItem } from '@library/api-menu-experience'
import type { RecipeURN, RecipeUUID } from '@library/menu-service'

import { RecipeCount } from '../CollectionScreen/RecipeCount'
import { RecipeTile } from './RecipeTile'
import type {
  RecipeTileCustomisationData,
  RecipeTileCustomisationSelection,
  RecipeTileFlexProps,
} from './RecipeTile/RecipeTile.types'

type RecipeGridTileState = {
  isInBasket: boolean
  quantity?: number
  canAddRecipes: boolean
  reachedLimit: boolean
}

type RecipeGridTileAction = (recipeId: RecipeUUID, recipePosition: number) => void
type RecipeGridCustomiseOpenAction = (
  recipeId: RecipeUUID,
  recipeUrn: RecipeURN | undefined,
  recipePosition: number,
) => void
type RecipeGridCustomisationSelectAction = (
  recipeId: RecipeUUID,
  customisationSelection: RecipeTileCustomisationSelection,
  recipePosition: number,
) => void

export type SupplementaryItem = {
  id: string
  position: number | 'end'
  content: React.ReactNode
}

const idleCustomisationData: RecipeTileCustomisationData = {
  isLoadingCustomisations: false,
  hasCustomisationError: false,
  hasOpenedCustomisations: false,
}

export type RecipeGridProps = {
  contentBlock: RecipeGridContentBlock
  recipeCountTextColor?: string
  supplementaryItems?: SupplementaryItem[]
  getRecipeGridItem?: (recipe: StandardRecipeItem) => StandardRecipeItem
  getRecipeTileState: (recipeId: RecipeUUID) => RecipeGridTileState
  getRecipeCustomisationData?: (recipeId: RecipeUUID) => RecipeTileCustomisationData
  customisationEducationRecipeId?: RecipeUUID | null
  onRecipeClick: RecipeGridTileAction
  onAddClick: RecipeGridTileAction
  onRemoveClick: RecipeGridTileAction
  onBlockedAddAttempt: () => void
  onCustomiseOpen?: RecipeGridCustomiseOpenAction
  onCustomiseClose?: () => void
  onCustomisationSelect?: RecipeGridCustomisationSelectAction
  onCustomisationRetry?: () => void
  onCustomisationEducationClose?: () => void
} & RecipeTileFlexProps

const recipeGridItemWidth: ResponsiveValue<string> = [
  '100%',
  '100%',
  'calc((100% - 16px) / 2)',
  'calc((100% - 32px) / 3)',
]

const isStandardRecipeItem = (item: StandardRecipeItem): item is StandardRecipeItem =>
  item.type === 'standard-recipe'

type GridItemProps = {
  children: React.ReactNode
}

const GridItem = ({ children }: GridItemProps) => (
  <Box
    display={Display.Flex}
    flexBasis={recipeGridItemWidth}
    flexGrow={0}
    flexShrink={0}
    maxWidth={recipeGridItemWidth}
  >
    {children}
  </Box>
)

export const RecipeGrid = ({
  contentBlock,
  recipeCountTextColor,
  supplementaryItems = [],
  getRecipeGridItem,
  getRecipeTileState,
  getRecipeCustomisationData,
  customisationEducationRecipeId,
  onRecipeClick,
  onAddClick,
  onRemoveClick,
  onBlockedAddAttempt,
  onCustomiseOpen,
  onCustomiseClose,
  onCustomisationSelect,
  onCustomisationRetry,
  onCustomisationEducationClose,
  renderFlexPortionBadge,
  renderFlexPortionEditor,
}: RecipeGridProps) => {
  const recipes = contentBlock.items.filter(isStandardRecipeItem)
  const numericSupplementaryItems = supplementaryItems.filter(
    (item): item is SupplementaryItem & { position: number } =>
      typeof item.position === 'number' && item.position > 0 && item.position < recipes.length,
  )
  const endSupplementaryItems = supplementaryItems.filter(({ position }) => position === 'end')

  const renderSupplementaryItems = (items: SupplementaryItem[]) =>
    items.map(({ id, content }) => <GridItem key={id}>{content}</GridItem>)

  return (
    <Box
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      gap={3}
      data-testid="recipe-grid"
    >
      {contentBlock.recipeCountLabel && (
        <Box paddingH={[3, 3, 0]}>
          <RecipeCount label={contentBlock.recipeCountLabel} textColor={recipeCountTextColor} />
        </Box>
      )}
      <Box
        display={Display.Flex}
        wrap
        justifyContent={JustifyContent.Center}
        gap={[3, 3, 4]}
        paddingH={[3, 3, 0]}
        data-testid="recipe-grid-items"
      >
        {recipes.map((item, index) => {
          const recipePosition = index + 1
          const recipe = getRecipeGridItem?.(item) ?? item
          const recipeTileState = getRecipeTileState(recipe.id)
          const customisationData = getRecipeCustomisationData?.(item.id) ?? idleCustomisationData

          return (
            <React.Fragment key={item.id}>
              <GridItem>
                <RecipeTile
                  recipe={recipe}
                  isInBasket={recipeTileState.isInBasket}
                  quantity={recipeTileState.quantity}
                  canAddRecipes={recipeTileState.canAddRecipes}
                  reachedLimit={recipeTileState.reachedLimit}
                  onClick={(recipeId) => onRecipeClick(recipeId, recipePosition)}
                  onAddClick={(recipeId) => onAddClick(recipeId, recipePosition)}
                  onRemoveClick={(recipeId) => onRemoveClick(recipeId, recipePosition)}
                  onBlockedAddAttempt={onBlockedAddAttempt}
                  customisationData={customisationData}
                  onCustomiseOpen={() => onCustomiseOpen?.(item.id, item.urn, recipePosition)}
                  onCustomiseClose={onCustomiseClose}
                  onCustomisationSelect={(customisationSelection) =>
                    onCustomisationSelect?.(item.id, customisationSelection, recipePosition)
                  }
                  onCustomisationRetry={onCustomisationRetry}
                  showCustomisationEducation={item.id === customisationEducationRecipeId}
                  onCustomisationEducationClose={onCustomisationEducationClose}
                  renderFlexPortionBadge={renderFlexPortionBadge}
                  renderFlexPortionEditor={renderFlexPortionEditor}
                />
              </GridItem>
              {renderSupplementaryItems(
                numericSupplementaryItems.filter(({ position }) => position === recipePosition),
              )}
            </React.Fragment>
          )
        })}
        {renderSupplementaryItems(endSupplementaryItems)}
      </Box>
    </Box>
  )
}
